import { getNativePlatform, isNativeApp } from "./platform";

/** Native only: fade the launch splash once the first screen has painted. */
export async function hideSplashScreen(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch {
    // Splash already gone.
  }
}

/**
 * Android only: hardware back. `handler` gets whether the WebView can go back;
 * without a listener Capacitor closes the app on the first press.
 */
export async function onHardwareBack(
  handler: (canGoBack: boolean) => void,
): Promise<() => void> {
  if (getNativePlatform() !== "android") return () => {};
  const { App } = await import("@capacitor/app");
  const handle = await App.addListener("backButton", (event) => {
    handler(event.canGoBack);
  });
  return () => {
    void handle.remove();
  };
}

/** Android only: leave the app (back pressed on the first screen). */
export async function exitApp(): Promise<void> {
  if (getNativePlatform() !== "android") return;
  const { App } = await import("@capacitor/app");
  await App.exitApp();
}

/** Native only: status bar glyphs light on dark UI, dark on light UI. */
export async function setStatusBarForTheme(darkUi: boolean): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: darkUi ? Style.Dark : Style.Light });
  } catch {
    // Not available on this OS version.
  }
}

export type HapticKind = "selection" | "light" | "medium" | "success";

/** Native only: short tactile tick for swipe, long-press and send. */
export async function haptic(kind: HapticKind): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import(
      "@capacitor/haptics"
    );
    if (kind === "selection") await Haptics.selectionChanged();
    else if (kind === "success")
      await Haptics.notification({ type: NotificationType.Success });
    else
      await Haptics.impact({
        style: kind === "medium" ? ImpactStyle.Medium : ImpactStyle.Light,
      });
  } catch {
    // Device without haptics.
  }
}
